import { UseQueryOptions } from '@tanstack/react-query';
import { parseISO } from 'date-fns';

import useAppQuery from 'hooks/useAppQuery';
import { BaseErrorResponse } from 'models/_base';
import {
  GetScheduledMessageByUidResponseTO,
  ScheduledMessageByUid,
  ScheduledMessageByUidApiTO,
} from 'models/automation/ScheduledMessages';
import API from 'services/API';
import { SCHEDULED_MESSAGES_BASE_QUERY_KEY } from './scheduledMessagesQuery';

type ReactQueryOptions = Omit<
  UseQueryOptions<ScheduledMessageByUid, BaseErrorResponse>,
  'queryKey' | 'queryFn'
>;

type UseFetchScheduledMessageByUidArgs = {
  scheduledMessageUid: string;
} & ReactQueryOptions;

const parseScheduledMessage = (
  data: ScheduledMessageByUidApiTO,
): ScheduledMessageByUid => {
  return {
    ...data,
    createdAt: data.createdAt ? parseISO(data.createdAt) : null,
    expectedFireTime: data.expectedFireTime
      ? parseISO(data.expectedFireTime)
      : null,
    updatedAt: data.updatedAt ? parseISO(data.updatedAt) : null,
  };
};

const useFetchScheduledMessageByUid = ({
  scheduledMessageUid,
  enabled = true,
  ...options
}: UseFetchScheduledMessageByUidArgs) => {
  return useAppQuery<ScheduledMessageByUid, BaseErrorResponse>({
    queryKey: [
      SCHEDULED_MESSAGES_BASE_QUERY_KEY,
      'detail',
      scheduledMessageUid,
    ],
    queryFn: async () => {
      const response = await API.get<GetScheduledMessageByUidResponseTO>(
        `/api/internal/triggers/scheduled-messages/${scheduledMessageUid}`,
      );

      return parseScheduledMessage(response.data.scheduledMessage);
    },
    enabled,
    staleTime: 30000,
    ...options,
  });
};

export default useFetchScheduledMessageByUid;
